import type { ReactNode } from 'react';

/** Props do componente PageHeader */
export interface PageHeaderProps {
  /** Título principal da página */
  readonly title: string;
  /** Subtítulo ou descrição opcional */
  readonly subtitle?: string;
  /** Slot para ações (botões, menus) — posicionado à direita */
  readonly actions?: ReactNode;
  /** Slot para breadcrumb — posicionado acima do título */
  readonly breadcrumb?: ReactNode;
}

/**
 * Organism PageHeader — cabeçalho padrão de página.
 *
 * Usado por todos os templates para manter consistência de layout.
 *
 * @example
 * ```tsx
 * <PageHeader
 *   title="Gerenciamento de Usuários"
 *   subtitle="Gerencie os usuários e permissões da plataforma"
 *   breadcrumb={
 *     <Breadcrumb>
 *       <BreadcrumbLink href="/"><BreadcrumbIcon /></BreadcrumbLink>
 *       <BreadcrumbLink href="/usuarios">Usuários</BreadcrumbLink>
 *       <BreadcrumbPage>Gerenciamento</BreadcrumbPage>
 *     </Breadcrumb>
 *   }
 *   actions={<Button onClick={openCreateModal}>Criar usuário</Button>}
 * />
 * ```
 */
export function PageHeader({ title, subtitle, actions, breadcrumb }: PageHeaderProps) {
  return (
    <header className="flex w-full flex-col gap-nano">
      {breadcrumb && <nav aria-label="breadcrumb">{breadcrumb}</nav>}

      <div className="flex flex-col gap-xs sm:flex-row sm:items-start sm:justify-between sm:gap-md">
        <div>
          <h1 className="text-ctx-content-title ts-heading-lg">{title}</h1>
          {subtitle && (
            <p className="mt-nano text-ctx-content-placeholder ts-paragraph-xs">{subtitle}</p>
          )}
        </div>

        {actions && (
          <div className="flex shrink-0 items-center gap-xs sm:self-start">{actions}</div>
        )}
      </div>
    </header>
  );
}
